import { useMutation, useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS } from 'constants/endpoints';
import { get, handleError, handleResponse, post, put, del } from './base.service';
import { useState } from 'react';

export const useJobCommonFieldsQuery = () =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_JOBS_DATA}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['job-common-fields'],
	});

export const usePopularJobsQuery = (params: string = '') =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_POPULAR_JOBS}?${params}`);
				const data = handleResponse(response);
				// Always return an object with popularJobs array
				return data ?? { popularJobs: [] };
			} catch (err) {
				handleError({
					success: false,
					message: err instanceof Error ? err.message : 'Unknown error',
				});
				return { popularJobs: [] };
			}
		},
		queryKey: ['popular-jobs'],
	});

export const useSaveNewJobMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.JOB, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const usePreferenceJobsQuery = (params: string = '') =>
	useQuery({
		queryFn: async () => {
			try {
				return await get(`${API_ENDPOINTS.GET_PREFERENCE_JOBS}?${params}`);
			} catch (err) {
				handleError({
					success: false,
					message: err instanceof Error ? err.message : 'Unknown error',
				});
			}
		},
		queryKey: ['preference-jobs'],
	});

export const useJobListQuery = (params: string = '') =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_JOB_LIST}?${params}`);
				return handleResponse(response);
			} catch (err) {
				handleError({
					success: false,
					message: err instanceof Error ? err.message : 'Unknown error',
				});
			}
		},
		queryKey: ['preference-jobs'],
	});

export const useJobListMutation = () =>
	useMutation({
		mutationFn: (params: string = '') => {
			return get(`${API_ENDPOINTS.GET_JOB_LIST}?${params}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useMarkAsFavouriteMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.JOB_MARK_AS_FAVORITE, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useMarkAsUnfavouriteMutation = () =>
	useMutation({
		mutationFn: (params: number | null = null) => {
			return del(`${API_ENDPOINTS.JOB_MARK_AS_UNFAVORITE}/${params}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useJobApplyMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.JOB_APPLY, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useJobFavouriteQuery = (params: string = '') =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.JOB_FAVORITE}?${params}`);
				return handleResponse(response);
			} catch (err) {
				handleError({
					success: false,
					message: err instanceof Error ? err.message : 'Unknown error',
				});
			}
		},
		queryKey: ['favourite-jobs'],
	});

export const useJobAppliedQuery = (params: string = '') =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.JOB_APPLIED}?${params}`);
				return handleResponse(response);
			} catch (err) {
				handleError({
					success: false,
					message: err instanceof Error ? err.message : 'Unknown error',
				});
			}
		},
		queryKey: ['applied-jobs'],
	});

export const useJobListFilterQuery = (initialEnabled = true) => {
	const [isQueryEnabled, setIsQueryEnabled] = useState(initialEnabled);
	const enableQuery = () => {
		setIsQueryEnabled(true);
	};
	if (!isQueryEnabled) {
		enableQuery();
	}
	return useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.JOB_LIST_FILTER}`);
				return handleResponse(response);
			} catch (err) {
				handleError({
					success: false,
					message: err instanceof Error ? err.message : 'Unknown error',
				});
			}
		},
		queryKey: ['job-list-filter'],
		enabled: isQueryEnabled,
	});
};

export const useSaveJobAlertMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.JOB_SAVE_ALERT, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useSavedJobAlertListQuery = (params: string = '') =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.JOB_SAVE_ALERT}?${params}`);
				return handleResponse(response);
			} catch (err) {
				handleError({
					success: false,
					message: err instanceof Error ? err.message : 'Unknown error',
				});
			}
		},
		queryKey: ['saved-job-alert'],
	});

export const useDeleteSavedJobAlertMutation = () =>
	useMutation({
		mutationFn: (params: number | null = null) => {
			return del(`${API_ENDPOINTS.JOB_SAVE_ALERT}/${params}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useJobDetailsQuery = (id: string = '') =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.JOB}/${id}`);
				return handleResponse(response);
			} catch (err) {
				handleError({
					success: false,
					message: err instanceof Error ? err.message : 'Unknown error',
				});
			}
		},
		queryKey: ['job-details'],
		enabled: !!id,
	});

export const usePostedJobQuery = (params: string = '') =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.JOB_POSTED}?${params}`);
				return handleResponse(response);
			} catch (err) {
				handleError({
					success: false,
					message: err instanceof Error ? err.message : 'Unknown error',
				});
			}
		},
		queryKey: ['posted-jobs'],
	});

export const useJobEditQuery = (id: string = '') =>
	useQuery({
		queryFn: async () => {
			try {
				return await get(`${API_ENDPOINTS.GET_JOB_DETAIL_EDIT}/${id}`);
			} catch (err) {
				handleError({
					success: false,
					message: err instanceof Error ? err.message : 'Unknown error',
				});
			}
		},
		queryKey: ['job-edit'],
		enabled: !!id,
	});

export const useJobUpdateMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return put(`${API_ENDPOINTS.JOB_UPDATE}/${payload?.id}`, payload?.values);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useDeleteJobMutation = () =>
	useMutation({
		mutationFn: (id: number | null = null) => {
			return del(`${API_ENDPOINTS.JOB}/${id}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useApplicantsListMutation = (params: string = '') =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_APPLICANTS_LIST}?${params}`);
				return handleResponse(response);
			} catch (err) {
				handleError({
					success: false,
					message: err instanceof Error ? err.message : 'Unknown error',
				});
			}
		},
		queryKey: ['popular-jobs'],
	});

export const useGetSeekerAssessmentQuery = (payload: any = {}) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await post(`${API_ENDPOINTS.GET_SEEKER_ASSESSMENT}`, payload);
				return handleResponse(response);
			} catch (err) {
				handleError({
					success: false,
					message: err instanceof Error ? err.message : 'Unknown error',
				});
			}
		},
		queryKey: ['posted-job-assesment'],
		enabled: !!payload?.jobId && !!payload?.applicantId,
		refetchOnWindowFocus: false,
	});

export const useInactiveJobListQuery = (query: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const { page, limit, inActive } = query;
				const response = await get(
					`${API_ENDPOINTS.GET_INACTIVE_JOB_LIST}?order=DESC&page=${page}&limit=${limit}&inActive=${inActive}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError({
					success: false,
					message: err instanceof Error ? err.message : 'Unknown error',
				});
			}
		},
		queryKey: ['inactive-job'],
	});

export const useExpiredJobListMutation = () =>
	useMutation({
		mutationFn: (query: any) => {
			const { page, limit, hasExpired } = query;
			return get(
				`${API_ENDPOINTS.GET_EXPIRED_JOB_LIST}?order=DESC&page=${page}&limit=${limit}&hasExpired=${hasExpired}`,
			);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useActiveJobMutation = () =>
	useMutation({
		mutationFn: (id: any) => {
			return put(`${API_ENDPOINTS.JOB_ACTIVE}/${id}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useGetJobAISuggestionsQuery = (payload: any = {}) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await post(`${API_ENDPOINTS.GET_JOB_AI_SUGGESTIONS}`, payload);
				return handleResponse(response);
			} catch (err) {
				handleError({
					success: false,
					message: err instanceof Error ? err.message : 'Unknown error',
				});
			}
		},
		queryKey: ['post-job-ai-suggestions', { payload }], // caching
		enabled: Array.isArray(payload) && payload.length > 0, // Prevents the query from running on empty
		refetchOnWindowFocus: false,
	});
